import React, { useState, useEffect, useMemo } from "react";
import { Nav } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./Sidebar.css";
import brandImage from "../../assets/mcollectbrandimage.jpg";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../redux/authSlice/authSlice";

const Sidebar = () => {
  const loginData = useSelector((store) => store.auth.loginData || {});
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();

  const MENU_CONFIG = [
    {
      key: "employees",
      title: "Employees",
      icon: "🙍",
      permission: "employee_management",
      submenu: [
        { title: "Employee List", path: "/employees/list" },
        { title: "Create Employee", path: "/employees/add" },
      ],
    },
    {
      key: "yards",
      title: "Yards",
      icon: "🏢",
      permission: "yard_management",
      submenu: [
        { title: "Yard List", path: "/yards/list" },
        { title: "Create Yard", path: "/yards/addYard" },
      ],
    },
    {
      key: "cases",
      title: "Cases",
      icon: "🚗",
      permission: "case_management",
      submenu: [
        { title: "Case List", path: "/cases/list" },
        { title: "Create Case", path: "/cases/add" },
        { title: "Assign Case", path: "/cases/AssignCasesList" },
      ],
    },
  ];

  // 🔑 CORE LOGIC (agency = full access)
  const menuItems = useMemo(() => {
    if (!loginData?.role) return [];

    if (loginData.role === "Agency") {
      return MENU_CONFIG;
    }

    return MENU_CONFIG.filter((item) =>
      loginData.permissions?.includes(item.permission)
    );
  }, [loginData]);

  const isActive = (path) => {
    if (!path) return false;
    if (path === "/") return location.pathname === "/";
    return (
      location.pathname === path ||
      location.pathname.startsWith(path + "/")
    );
  };

  const isSubmenuActive = (submenuItems) =>
    submenuItems?.some((item) => isActive(item.path));

  const handleDropdownToggle = (key) => {
    setOpenDropdown((prev) => (prev === key ? null : key));
  };

  const handleLinkClick = () => {
    setOpenDropdown(null);
  };

  // Auto-open active submenu
  useEffect(() => {
    const activeDropdown = menuItems.find(
      (m) => m.submenu && isSubmenuActive(m.submenu)
    );
    if (activeDropdown) {
      setOpenDropdown(activeDropdown.key);
    }
  }, [location.pathname, menuItems]);

  const handleLogOut = async () => {
    await dispatch(logout());
  };

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <div className="brand-container" onClick={() => navigate("/")}>
          <div className="brand-icon">
            <img src={brandImage} alt="mCollect brand icon" />
          </div>
          <h3 className="brand-name">mCollect Agency Panel</h3>
        </div>
      </div>

      <Nav className="flex-column sidebar-nav">
        {menuItems.map((item) => {
          const isItemActive = isSubmenuActive(item.submenu);
          const isDropdownOpen = openDropdown === item.key;

          return (
            <div key={item.key} className="nav-item-wrapper">
              <div
                className={`nav-link-main ${isItemActive ? "active" : ""}`}
                onClick={() => handleDropdownToggle(item.key)}
                role="button"
                tabIndex={0}
              >
                <span className="nav-icon">{item.icon}</span>
                <span className="nav-text">{item.title}</span>
                <span
                  className={`dropdown-arrow ${
                    isDropdownOpen ? "open" : ""
                  }`}
                >
                  ▾
                </span>
              </div>

              <div className={`submenu ${isDropdownOpen ? "open" : ""}`}>
                {item.submenu.map((subItem) => (
                  <Link
                    key={subItem.path}
                    to={subItem.path}
                    className={`submenu-item ${
                      isActive(subItem.path) ? "active" : ""
                    }`}
                    onClick={handleLinkClick}
                  >
                    <span className="submenu-dot"></span>
                    {subItem.title}
                  </Link>
                ))}
              </div>
            </div>
          );
        })}
      </Nav>

      <div className="sidebar-footer">
        <div className="user-info mb-2">
          <div className="user-avatar">
            <span className="user-icon">👤</span>
          </div>
          <div className="user-details">
            <span className="user-role">{loginData?.name}</span>
          </div>
        </div>

        <div
          className="user-info d-flex justify-content-center align-items-center"
          onClick={handleLogOut}
          role="button"
        >
          <span>Log Out</span>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
